import React, { useState } from "react";
import { logError } from "../Utilities/errorLogger";

function ContactForm({ closeModal, onSubmissionStatusChange }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    onSubmissionStatusChange(true);

    try {
      const response = await fetch("/api/email/sendContactUsEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setFormData({ name: "", email: "", subject: "", message: "" });
        closeModal();
      } else {
        setError(data.message || "Failed to send message. Please try again.");
      }
    } catch (error) {
      await logError({
        filePath: "ReusableComponents/ContactUsForm.js",
        functionName: "handleSubmit",
        errorType: "Error",
        errorMessage: error.message,
        stackTrace: error.stack,
      });
      console.error("Error sending email:", error);
      setError("Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
      onSubmissionStatusChange(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all";

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
        <p className="text-sm text-gray-500 mt-1">
          Have a question or feedback? We'd love to hear from you.
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1.5">
            Your Name
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            placeholder="Jane Doe"
            value={formData.name}
            className={inputClass}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1.5">
            Your Email
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            placeholder="you@example.com"
            value={formData.email}
            className={inputClass}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-1.5">
            Subject
          </label>
          <input
            id="contact-subject"
            type="text"
            name="subject"
            placeholder="What's this about?"
            value={formData.subject}
            className={inputClass}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1.5">
            Your Message
          </label>
          <textarea
            id="contact-message"
            name="message"
            placeholder="Tell us a bit more..."
            value={formData.message}
            className={`${inputClass} min-h-[140px] resize-y`}
            onChange={handleChange}
            required
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={closeModal}
            disabled={isSubmitting}
            className="flex-1 py-3 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Sending..." : "Send Message"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ContactForm;
